// CalendarScreen - §13 일정관리.
//
// **읽기 전용이다.** 일정은 상세화면에서 입력하고 여기 자동 반영된다
// (§13 원문, REQ-CAL-004). 그래서 셀에 [+] 버튼이 없고, 드래그도 없고,
// 빈 셀 클릭도 아무 일을 하지 않는다. 같은 내용을 두 화면에서 입력하지
// 않는다 (§2).
//
// 이벤트의 색은 **일정 종류가 아니라 상태**다. 8종에 각각 색을 주면 8색
// 팔레트가 되고 액센트 락이 무너진다(디자인 가이드 §4). 종류는 텍스트
// 라벨이 말한다.
import { StatusDot } from "@/components/StatusIndicator";
import {
  Button,
  Chip,
  DataBoundary,
  Dialog,
  FilterBar,
  Icon,
  Select,
  Skeleton,
} from "@/components/ui";
import type { CalendarEvent, Customer, Staff } from "@/lib/api";
import {
  type DayCell,
  WEEKDAY_LABELS,
  foldEvents,
  gridRange,
  groupByDate,
  monthGrid,
  monthOf,
  shiftMonth,
  viewTitle,
  weekDays,
} from "@/lib/calendar";
import { addDays, isoDate, parseIsoDate } from "@/lib/dates";
import { useCalendar } from "@/lib/hooks";
import { CALENDAR_EVENT_LABELS, statusKey, todayKst } from "@/lib/status";
import type { ScreenNav } from "@/screens/nav";
import { useState } from "react";

const PRODUCTION = "production";

/** 범례 순서는 §13 표시대상 순서 그대로. 생산계획은 별도 토글이다. */
const LEGEND: readonly { key: string; label: string }[] = [
  ...Object.entries(CALENDAR_EVENT_LABELS).map(([key, label]) => ({ key, label })),
  { key: PRODUCTION, label: "생산계획" },
];

const kindOf = (e: CalendarEvent): string => e.milestoneKey ?? PRODUCTION;

export const CalendarScreen = ({
  nav,
  customers,
  staff,
}: {
  nav: ScreenNav;
  customers: readonly Customer[];
  staff: readonly Staff[];
}) => {
  const [today] = useState(() => todayKst());
  const [anchor, setAnchor] = useState(today);
  const [view, setView] = useState<"month" | "week">("month");
  const [customerId, setCustomerId] = useState("");
  const [staffId, setStaffId] = useState("");
  const [includeProduction, setIncludeProduction] = useState(true);
  const [hidden, setHidden] = useState<readonly string[]>([]);
  const [dayOpen, setDayOpen] = useState<string | null>(null);

  const [from, to] = gridRange(view, anchor);
  const res = useCalendar(from, to, { customerId, staffId, includeProduction });

  const visible = res.data.filter((e) => !hidden.includes(kindOf(e)));
  const byDate = groupByDate(visible);
  const filtered = !!customerId || !!staffId || hidden.length > 0 || !includeProduction;

  const shift = (delta: number) =>
    setAnchor(
      view === "month"
        ? `${shiftMonth(monthOf(anchor), delta)}-01`
        : isoDate(addDays(parseIsoDate(anchor), delta * 7))
    );

  const reset = () => {
    setCustomerId("");
    setStaffId("");
    setIncludeProduction(true);
    setHidden([]);
  };

  const weeks = view === "month" ? monthGrid(monthOf(anchor), today) : [weekDays(anchor, today)];

  return (
    <div className="flex flex-col gap-s4">
      <div className="overflow-hidden rounded-card" style={{ background: "var(--surface)" }}>
        <FilterBar
          right={
            <>
              <Button
                variant="ghost"
                size="sm"
                icon={<Icon name="chevL" size={14} />}
                onClick={() => shift(-1)}
              >
                <span className="sr-only">이전</span>
              </Button>
              <span
                className="min-w-[9rem] text-center text-label font-semibold tabular-nums"
                data-num=""
              >
                {viewTitle(view, anchor)}
              </span>
              <Button
                variant="ghost"
                size="sm"
                icon={<Icon name="chevR" size={14} />}
                onClick={() => shift(1)}
              >
                <span className="sr-only">다음</span>
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setAnchor(today)}>
                오늘
              </Button>
              <Chip active={view === "month"} onClick={() => setView("month")}>
                월간
              </Chip>
              <Chip active={view === "week"} onClick={() => setView("week")}>
                주간
              </Chip>
            </>
          }
        >
          <span className="w-[9rem]">
            <Select
              value={customerId}
              onChange={setCustomerId}
              options={customers
                .filter((c) => c.active)
                .map((c) => ({ value: c.id, label: c.name }))}
              ariaLabel="고객사로 조회"
              placeholder="고객사 전체"
            />
          </span>
          <span className="w-[9rem]">
            <Select
              value={staffId}
              onChange={setStaffId}
              options={staff.filter((s) => s.active).map((s) => ({ value: s.id, label: s.name }))}
              ariaLabel="담당자로 조회"
              placeholder="담당자 전체"
            />
          </span>
        </FilterBar>

        {/* 범례가 곧 종류 필터다. 별도 필터 UI 를 두지 않는다. */}
        <div
          className="flex flex-wrap items-center gap-s2 px-s4 pb-s3"
          style={{ borderBottom: "1px solid var(--border-subtle)" }}
        >
          <span
            className="text-micro font-semibold uppercase"
            style={{ color: "var(--text-tertiary)" }}
          >
            범례
          </span>
          {LEGEND.map((l) => {
            const on = l.key === PRODUCTION ? includeProduction : !hidden.includes(l.key);
            return (
              <Chip
                key={l.key}
                active={on}
                onClick={() => {
                  if (l.key === PRODUCTION) {
                    setIncludeProduction(!includeProduction);
                    return;
                  }
                  setHidden((prev) =>
                    prev.includes(l.key) ? prev.filter((k) => k !== l.key) : [...prev, l.key]
                  );
                }}
              >
                {l.label}
              </Chip>
            );
          })}
          {filtered && (
            <Button variant="ghost" size="sm" onClick={reset}>
              필터 초기화
            </Button>
          )}
        </div>

        <DataBoundary
          loading={res.loading}
          error={res.error}
          onRetry={() => void res.reload()}
          skeleton={
            <div className="grid grid-cols-7 gap-px p-s4">
              {Array.from({ length: 35 }, (_, i) => (
                <Skeleton key={i} h={112} />
              ))}
            </div>
          }
        >
          {/* 이벤트가 0건이어도 그리드를 없애지 않는다. 달력은 달력이고,
              날짜 맥락 자체가 정보다 (§04 §4 빈 상태). */}
          {visible.length === 0 && (
            <p className="px-s4 py-s3 text-label" style={{ color: "var(--text-tertiary)" }}>
              {filtered
                ? "선택한 조건에 해당하는 일정이 없어요."
                : view === "month"
                  ? "이번 달 등록된 주요 일정이 없어요."
                  : "이번 주 등록된 주요 일정이 없어요."}
            </p>
          )}

          <div className="p-s4">
            <div
              className="grid grid-cols-7 text-micro font-semibold uppercase"
              style={{ color: "var(--text-tertiary)" }}
            >
              {WEEKDAY_LABELS.map((d) => (
                <span key={d} className="px-s2 py-s2">
                  {d}
                </span>
              ))}
            </div>
            <div
              className="grid grid-cols-7 gap-px rounded-card"
              style={{ background: "var(--border-subtle)" }}
            >
              {weeks.flat().map((cell) => (
                <CalendarCellView
                  key={cell.date}
                  cell={cell}
                  events={byDate[cell.date] ?? []}
                  onOpenItem={nav.openItem}
                  onOpenDay={() => setDayOpen(cell.date)}
                />
              ))}
            </div>
          </div>
        </DataBoundary>
      </div>

      {/* `+N건` 은 그 날짜의 전체 목록을 보여준다 (REQ-CAL-008). 팝오버
          프리미티브가 없어서 Dialog 를 쓴다 - 포커스 트랩이 공짜다. */}
      <Dialog
        open={dayOpen !== null}
        onClose={() => setDayOpen(null)}
        title={dayOpen ? `${dayOpen} 일정` : "일정"}
        width={420}
      >
        <ul className="flex list-none flex-col gap-s3 p-0">
          {(dayOpen ? (byDate[dayOpen] ?? []) : []).map((e) => (
            <li key={`${e.kind}:${e.targetId}`} className="flex items-center gap-s2">
              <StatusDot status={statusKey(e.status)} size="md" />
              <span className="text-caption" style={{ color: "var(--text-tertiary)" }}>
                {e.eventLabel}
              </span>
              <Button
                variant="link"
                size="sm"
                onClick={() => {
                  setDayOpen(null);
                  nav.openItem(e.devItemId);
                }}
              >
                {`${e.customerName} ${e.partName}`}
              </Button>
            </li>
          ))}
        </ul>
      </Dialog>
    </div>
  );
};

/** 셀 높이는 고정이다. 늘어나면 월 전체 조망이 깨진다 (결정 S-8). */
const CalendarCellView = ({
  cell,
  events,
  onOpenItem,
  onOpenDay,
}: {
  cell: DayCell;
  events: readonly CalendarEvent[];
  onOpenItem: (id: string) => void;
  onOpenDay: () => void;
}) => {
  const { shown, hidden } = foldEvents(events, 3);
  const hasLate = events.some((e) => e.status === "red");
  return (
    <div
      className="flex min-h-[7rem] flex-col gap-s1 p-s2"
      style={{
        background: hasLate
          ? "var(--row-late-bg)"
          : cell.inMonth
            ? "var(--surface)"
            : "var(--surface-inset)",
      }}
    >
      <span className="flex items-center gap-s1">
        <span
          className="inline-flex h-5 min-w-5 items-center justify-center rounded-pill px-s1 text-caption font-semibold tabular-nums"
          data-num=""
          style={
            cell.isToday
              ? { background: "var(--accent)", color: "var(--text-on-accent)" }
              : {
                  color: cell.inMonth ? "var(--text-secondary)" : "var(--text-quaternary)",
                }
          }
        >
          {cell.day}
        </span>
        {cell.isToday && <span className="sr-only">오늘</span>}
      </span>

      {shown.map((e) => (
        <span key={`${e.kind}:${e.targetId}`} className="flex items-center gap-s1 overflow-hidden">
          <StatusDot status={statusKey(e.status)} size="row" title={`${e.eventLabel} 상태`} />
          <Button variant="link" size="sm" onClick={() => onOpenItem(e.devItemId)}>
            <span className="truncate text-micro">{`${e.eventLabel} ${e.partName}`}</span>
          </Button>
        </span>
      ))}

      {hidden > 0 && (
        <Button variant="ghost" size="sm" onClick={onOpenDay}>
          <span className="text-micro tabular-nums" data-num="">
            {`+${hidden}건`}
          </span>
        </Button>
      )}
    </div>
  );
};
